import { Link } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityComboboxMulti } from "@/components/ui/entity-combobox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type RoleCertificationRow,
  type RoleCompetencyRow,
  type RoleSkillRow,
  useRoleCertifications,
  useRoleCompetencies,
  useRoleSkills,
  useSetRoleCertifications,
  useSetRoleCompetencies,
  useSetRoleSkills,
} from "@/hooks/use-role-requirements";
import { originLinkSearch } from "@/lib/detail-origin";

/**
 * Deliberately not exported — callers pass string literals, and an unused export
 * fails knip.
 */
type RequirementSection = "skills" | "competencies" | "certifications";

interface Props {
  roleId: string;
  roleName: string;
  isAdmin: boolean;
  /**
   * What this tab covers. The edit dialog offers **exactly these** — since
   * certifications moved to their own tab, editing them from the capabilities
   * tab (or vice versa) would put controls where the reader isn't looking.
   */
  sections: RequirementSection[];
  /**
   * Whether to render the flat chip lists. False on a role whose level
   * expectation grid already names every skill and competency — the Edit
   * affordance still shows, because the grid is read-only about *which*
   * capabilities the role requires.
   */
  showLists: boolean;
}

const SECTION_LABEL: Record<RequirementSection, string> = {
  skills: "skills",
  competencies: "competencies",
  certifications: "certifications",
};

/** "skills and competencies" / "certifications" — for the dialog's wording. */
function describeSections(sections: RequirementSection[]): string {
  const words = sections.map((s) => SECTION_LABEL[s]);
  if (words.length <= 1) return words[0] ?? "requirements";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

export function RoleRequirementsEditor({ roleId, roleName, isAdmin, sections, showLists }: Props) {
  const skills = useRoleSkills(roleId);
  const competencies = useRoleCompetencies(roleId);
  const certifications = useRoleCertifications(roleId);
  const [editing, setEditing] = useState(false);

  // With a single section the tab's own title already names it ("Required
  // certifications"), so an inner heading would just say it twice.
  const showHeadings = sections.length > 1;

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Edit required {describeSections(sections)}
          </Button>
        </div>
      )}

      {showLists && sections.includes("skills") && (
        <div className="space-y-3">
          {showHeadings && <h3 className="text-sm font-medium">Skills</h3>}
          {skills.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : skills.isError ? (
            <ErrorState
              className="py-4"
              message="Required skills couldn't be loaded."
              onRetry={() => skills.refetch()}
            />
          ) : (skills.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No required skills defined.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {(skills.data ?? []).map((s) => (
                <li
                  key={s.id}
                  className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm"
                >
                  <Link
                    to="/dashboard/skills/$skillId"
                    params={{ skillId: s.skillId }}
                    search={originLinkSearch("role", roleId, roleName)}
                    className="text-primary hover:underline"
                  >
                    {s.skillName}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showLists && sections.includes("competencies") && (
        <div className="space-y-3">
          {showHeadings && <h3 className="text-sm font-medium">Competencies</h3>}
          {competencies.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : competencies.isError ? (
            <ErrorState
              className="py-4"
              message="Required competencies couldn't be loaded."
              onRetry={() => competencies.refetch()}
            />
          ) : (competencies.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No required competencies defined.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {(competencies.data ?? []).map((c) => (
                <li key={c.id}>
                  <Link
                    to="/dashboard/competencies/$competencyId"
                    params={{ competencyId: c.competencyId }}
                    search={originLinkSearch("role", roleId, roleName)}
                    className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm text-primary hover:bg-muted/50 hover:underline"
                  >
                    {c.competencyName}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showLists && sections.includes("certifications") && (
        <div className="space-y-3">
          {showHeadings && <h3 className="text-sm font-medium">Certifications</h3>}
          {certifications.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : certifications.isError ? (
            <ErrorState
              className="py-4"
              message="Required certifications couldn't be loaded."
              onRetry={() => certifications.refetch()}
            />
          ) : (certifications.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No required certifications defined.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {(certifications.data ?? []).map((c) => (
                <li key={c.id}>
                  <Link
                    to="/dashboard/certifications/$certificationId"
                    params={{ certificationId: c.certificationId }}
                    search={originLinkSearch("role", roleId, roleName)}
                    className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm text-primary hover:bg-muted/50 hover:underline"
                  >
                    {c.certificationName}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isAdmin && editing && (
        <RequirementsEditDialog
          open
          onOpenChange={(o) => setEditing(o)}
          sections={sections}
          roleId={roleId}
          currentSkills={skills.data ?? []}
          currentCompetencies={competencies.data ?? []}
          currentCertifications={certifications.data ?? []}
        />
      )}
    </div>
  );
}

/**
 * Edits only the sections its tab owns. Certifications live on a separate tab
 * now, so a dialog opened from Required capabilities must not quietly rewrite
 * them (and the reverse) — an untouched set would otherwise be saved back from
 * a draft the admin never saw.
 */
function RequirementsEditDialog({
  open,
  onOpenChange,
  sections,
  roleId,
  currentSkills,
  currentCompetencies,
  currentCertifications,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: RequirementSection[];
  roleId: string;
  currentSkills: RoleSkillRow[];
  currentCompetencies: RoleCompetencyRow[];
  currentCertifications: RoleCertificationRow[];
}) {
  const setSkills = useSetRoleSkills(roleId);
  const setComps = useSetRoleCompetencies(roleId);
  const setCerts = useSetRoleCertifications(roleId);

  const [skillDraft, setSkillDraft] = useState<string[]>([]);
  const [compDraft, setCompDraft] = useState<string[]>([]);
  const [certDraft, setCertDraft] = useState<string[]>([]);

  // Seed the drafts ONCE per open-session. The current* arrays come from parent
  // queries that refetch on window refocus, handing new array references;
  // without this guard the effect would re-fire and discard the admin's unsaved
  // selection. The ref resets on close.
  const seededForOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      seededForOpen.current = false;
      return;
    }
    if (seededForOpen.current) return;
    setSkillDraft(currentSkills.map((s) => s.skillId));
    setCompDraft(currentCompetencies.map((c) => c.competencyId));
    setCertDraft(currentCertifications.map((c) => c.certificationId));
    seededForOpen.current = true;
  }, [open, currentSkills, currentCompetencies, currentCertifications]);

  async function save() {
    try {
      // Only what this tab owns — see the note on this component.
      if (sections.includes("skills")) {
        await setSkills.mutateAsync(skillDraft.map((id) => ({ skillId: id })));
      }
      if (sections.includes("competencies")) {
        await setComps.mutateAsync(compDraft.map((id) => ({ competencyId: id })));
      }
      if (sections.includes("certifications")) {
        await setCerts.mutateAsync(certDraft.map((id) => ({ certificationId: id })));
      }
      onOpenChange(false);
    } catch {
      // Each mutation toasts its own error; keep the dialog open so the admin's
      // selection isn't lost (a failure partway through leaves the earlier sets
      // saved and later ones not — reopening shows the true saved state).
    }
  }

  const pending = setSkills.isPending || setComps.isPending || setCerts.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit required {describeSections(sections)}</DialogTitle>
          <DialogDescription>
            Pick the active {describeSections(sections)} required for this role.
          </DialogDescription>
        </DialogHeader>

        {sections.includes("skills") && (
          <section className="space-y-3">
            <h3 className="text-sm font-medium">Skills</h3>
            <EntityComboboxMulti
              entity="skills"
              value={skillDraft}
              onChange={setSkillDraft}
              filters={{ status: "active" }}
              placeholder="Search and select skills…"
              searchPlaceholder="Search skills…"
            />
          </section>
        )}

        {sections.includes("competencies") && (
          <section className="space-y-3">
            <h3 className="text-sm font-medium">Competencies</h3>
            <EntityComboboxMulti
              entity="competencies"
              value={compDraft}
              onChange={setCompDraft}
              filters={{ status: "active" }}
              placeholder="Search and select competencies…"
              searchPlaceholder="Search competencies…"
            />
          </section>
        )}

        {sections.includes("certifications") && (
          <section className="space-y-3">
            <h3 className="text-sm font-medium">Certifications</h3>
            <EntityComboboxMulti
              entity="certifications"
              value={certDraft}
              onChange={setCertDraft}
              filters={{ status: "active" }}
              placeholder="Search and select certifications…"
              searchPlaceholder="Search certifications…"
            />
          </section>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={pending}>
            {pending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
